import { logger } from "./logger";

// Cornerstone3D's RenderingEngine/IViewport types aren't re-exported from the
// app surface (utils/renderingEngine uses `any` for the same reason). Define
// the slice of the API this helper depends on so callers stay typed without
// pulling the full Cornerstone types into utils/.
//
// `setViewPresentation` accepts `any` so concrete Cornerstone viewport types
// (whose parameter is the narrower `ViewPresentation`) remain structurally
// assignable through the contravariant parameter position. The value passed
// is always the matching `getViewPresentation` return — never user-shaped.
type ViewPresentationLike = any;

export interface ResizableViewport {
  id?: string;
  element?: HTMLDivElement;
  getViewPresentation?: () => ViewPresentationLike;
  setViewPresentation?: (pres: ViewPresentationLike) => void;
  getSliceIndex?: () => number;
  getVolumeId?: () => string | null;
}

export interface ResizableEngine {
  getViewports?: () => ResizableViewport[];
  resize: (immediate: boolean, keepCamera: boolean) => void;
}

export interface JumpToSlice {
  // Cornerstone3D's `utilities.jumpToSlice` is async (returns Promise<void>);
  // type as `void | PromiseLike<void>` so the helper can attach a `.catch`
  // without TS complaining and so test stubs can return either shape.
  (
    element: HTMLDivElement,
    opts: { imageIndex: number; volumeId?: string },
  ): void | PromiseLike<void>;
}

export interface ResizeAndRestoreOptions {
  /** Cornerstone3D `utilities.jumpToSlice`. Injected so callers don't need
   *  to import getCornerstone here, and so tests can stub it. */
  jumpToSlice: JumpToSlice;
  /** Optional sliceIndex override per viewport, keyed by viewport id. When
   *  present, takes precedence over vp.getSliceIndex() — used by the hanging-
   *  protocol path where the slot atom is the authoritative source. */
  sliceIndexOverrides?: Map<string, number>;
  /** Used in debug log prefixes so per-surface failures stay attributable. */
  logContext: string;
}

/**
 * Select the viewports that are actually on-screen (non-zero rendered size).
 *
 * Non-active layouts hide their unused slots with `display:none` (2×1 hides
 * the bottom row, single hides three slots), which collapses those elements
 * to 0×0. Cornerstone's `engine.resize` already skips 0×0 canvases
 * (`ContextPoolRenderingEngine._resizeVTKViewports` `continue`s on
 * displayedWidth/Height === 0), so a hidden slot neither stretches nor gets a
 * corrupted camera — capturing/restoring it is pointless and a `jumpToSlice`
 * on its detached 0×0 element is just noise. A viewport with no element, or a
 * degenerate aspect (zero on either axis), is treated as off-screen.
 *
 * An empty return means every viewport collapsed — the grid panel itself is
 * hidden, not a layout swap. Callers treat that as a no-op (and should log,
 * since a layout that consistently traps the grid here is an ops signal, not
 * a "resize never happened" silence).
 */
export function selectOnScreenViewports(viewports: ResizableViewport[]): ResizableViewport[] {
  return viewports.filter((vp) => {
    const rect = vp.element?.getBoundingClientRect();
    return rect != null && rect.width > 0 && rect.height > 0;
  });
}

/**
 * Canonical Cornerstone3D resize pattern, batched across viewports:
 *
 *   1. Capture each viewport's ViewPresentation + slice index BEFORE resize
 *   2. Run engine.resize(true, false) once — resetCameraForResize re-fits
 *      every viewport's projection to its new canvas aspect, fixing the
 *      keepCamera=true stretch (#356)
 *   3. Restore presentation (pan/zoom/rotation/flip) per viewport
 *   4. Restore slice via jumpToSlice — ViewPresentation doesn't carry it
 *
 * Why a helper: the HangingProtocolGrid path (coordinated across 4 slots)
 * and the standalone VolumeViewport path (CaseViewer fallback) need exactly
 * this sequence. Two copies drift; one shared helper means the next bug in
 * this pattern gets fixed once.
 */
export function resizeAndRestoreViewports(
  engine: ResizableEngine,
  viewports: ResizableViewport[],
  opts: ResizeAndRestoreOptions,
): void {
  const captured = viewports.map((vp) => {
    const idOverride =
      vp.id && opts.sliceIndexOverrides ? opts.sliceIndexOverrides.get(vp.id) : undefined;
    const sliceFromViewport = typeof vp.getSliceIndex === "function" ? vp.getSliceIndex() : null;
    return {
      vp,
      pres: typeof vp.getViewPresentation === "function" ? vp.getViewPresentation() : null,
      sliceIndex: idOverride != null ? idOverride : sliceFromViewport,
      volumeId: typeof vp.getVolumeId === "function" ? vp.getVolumeId() : null,
      element: vp.element,
    };
  });

  engine.resize(true, false);

  for (const { vp, pres, sliceIndex, volumeId, element } of captured) {
    if (pres && typeof vp.setViewPresentation === "function") {
      try {
        vp.setViewPresentation(pres);
      } catch (err) {
        // A failed restore is the exact regression this helper is supposed
        // to prevent — surface in prod (logger.debug is a no-op there).
        logger.warn(`[${opts.logContext}] setViewPresentation:`, err);
      }
    }
    if (element && sliceIndex != null && sliceIndex >= 0) {
      try {
        // jumpToSlice is async — sync try/catch alone leaks the rejection as
        // "Uncaught (in promise)". Attach a .catch so the warning surfaces
        // through our logger and the rejection is consumed.
        const result = opts.jumpToSlice(element, {
          imageIndex: sliceIndex,
          volumeId: volumeId ?? undefined,
        });
        if (result && typeof (result as PromiseLike<void>).then === "function") {
          Promise.resolve(result).catch((err: unknown) => {
            logger.warn(`[${opts.logContext}] jumpToSlice:`, err);
          });
        }
      } catch (err) {
        logger.warn(`[${opts.logContext}] jumpToSlice:`, err);
      }
    }
  }
}
